import React, { useEffect, useState } from "react";
import { api, money } from "@/lib/api";
import { StatusPill, EmptyState, InlineLoader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import ActionDialog from "@/components/admin/ActionDialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Banknote, CheckCircle2, PauseCircle, XCircle, Inbox, Clock, ShieldCheck, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

export default function PayoutQueue() {
  const [queue, setQueue] = useState(null);

  const load = () => api.get("/admin/payout-queue").then((r) => setQueue(r.data)).catch(() => setQueue([]));
  useEffect(() => { load(); }, []);

  const act = async (id, action, notes) => {
    try {
      await api.post(`/admin/payouts/${id}/${action}`, { notes });
      toast.success(`Payout ${action}d.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not ${action} payout.`);
    }
  };

  if (queue === null) return <InlineLoader label="Loading payout queue" />;

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-extrabold tracking-tight text-ff-blue sm:text-3xl">Payout Queue</h1>
        <p className="mt-1 text-sm text-ff-medium">Payouts release only through Stripe rails after review readiness.</p>
      </div>

      {queue.length === 0 ? (
        <EmptyState icon={Inbox} title="No payouts pending" message="There are no payout requests to review." testid="payout-queue-empty" />
      ) : (
        <div className="overflow-hidden rounded-xl border border-ff-border bg-white shadow-sm" data-testid="admin-payout-queue-table">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F3F4F6]">
                <TableHead>Campaign</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Risk</TableHead>
                <TableHead>KYC</TableHead>
                <TableHead>Hold until</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {queue.map((p) => (
                <TableRow key={p.id} data-testid={`payout-row-${p.id}`}>
                  <TableCell><div className="font-heading font-bold text-ff-blue">{p.campaign_title}</div></TableCell>
                  <TableCell className="text-right font-semibold text-ff-blue">{money(p.amount)}</TableCell>
                  <TableCell>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${p.risk_tier === "elevated" ? "bg-[#FEF2F2] text-ff-red" : "bg-[#F3F4F6] text-ff-dark"}`}>{p.risk_tier || "standard"}</span>
                  </TableCell>
                  <TableCell>
                    {p.identity_verified ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-[#ECFDF3] px-2 py-0.5 text-xs font-bold text-ff-green" data-testid={`kyc-verified-${p.id}`}>
                        <ShieldCheck className="h-3 w-3" /> Verified
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-[#FEF2F2] px-2 py-0.5 text-xs font-bold text-ff-red" data-testid={`kyc-unverified-${p.id}`}>
                        <ShieldAlert className="h-3 w-3" /> Not verified
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-ff-medium">
                    {p.hold_until ? <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{new Date(p.hold_until).toLocaleDateString()}</span> : "—"}
                  </TableCell>
                  <TableCell><StatusPill status={p.status} /></TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <ActionDialog testid={`release-${p.id}`} trigger={<Button size="sm" className="bg-ff-green text-white hover:bg-ff-green/90" data-testid={`release-btn-${p.id}`}><CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Release</Button>}
                        title="Release payout" description="Funds release through Stripe rails. Document the reason." requireNotes confirmLabel="Release funds" onConfirm={(n) => act(p.id, "release", n)} />
                      <ActionDialog testid={`hold-${p.id}`} trigger={<Button size="sm" variant="outline" className="border-ff-amber text-ff-amber"><PauseCircle className="mr-1 h-3.5 w-3.5" /> Hold</Button>}
                        title="Hold payout" requireNotes confirmLabel="Place hold" onConfirm={(n) => act(p.id, "hold", n)} />
                      <ActionDialog testid={`reject-payout-${p.id}`} trigger={<Button size="sm" variant="ghost" className="text-ff-red"><XCircle className="h-3.5 w-3.5" /></Button>}
                        title="Reject payout" requireNotes destructive confirmLabel="Reject" onConfirm={(n) => act(p.id, "reject", n)} />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
